import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="text-lg md:text-xl font-heading font-bold text-foreground-950 tracking-tight">
              CSSSpreadsheet<span className="text-primary-500">.</span>study
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shapes-line w-4 h-4 flex items-center justify-center"></i>
                Categories
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[580px] bg-background-50 rounded-xl border border-background-200/70 shadow-lg p-4 animate-scale-in">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-50 transition-colors group cursor-pointer"
                      >
                        <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors shrink-0">
                          <i className={`${cat.icon} text-foreground-700 group-hover:text-primary-700 text-base w-4 h-4 flex items-center justify-center`}></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground-900 whitespace-nowrap">{cat.name}</div>
                          <div className="text-xs text-foreground-500">{cat.description}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/article/cssbuy-sheet-complete-guide"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Buying Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium bg-foreground-950 text-background-50 px-5 py-2.5 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              cssbuy sheet
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl w-5 h-5 flex items-center justify-center`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-fade-in">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100"
            >
              Home
            </Link>

            <div className="px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              Categories
            </div>
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100 cursor-pointer"
              >
                <i className={`${cat.icon} text-foreground-500 w-4 h-4 flex items-center justify-center`}></i>
                {cat.name}
              </a>
            ))}

            <div className="border-t border-background-200/70 pt-3 mt-3">
              <Link
                to="/article/cssbuy-sheet-complete-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-3 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100"
              >
                Buying Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block mx-3 mt-2 px-5 py-3 text-sm font-medium bg-foreground-950 text-background-50 rounded-full text-center hover:bg-foreground-800 transition-colors cursor-pointer"
              >
                cssbuy sheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}